import { z } from 'zod'

import { cognitoLogin, cognitoLogout } from '@/auth/cognito'
import type { AddonModuleKey } from '@/modules/module-keys'

import { request, type QueryValue } from './transport'
import type { CatalogFilters } from './queryKeys'
import {
  activityEntrySchema,
  catalogSchemaSchema,
  catalogSearchResponseSchema,
  collectionSchema,
  companyPatchSchema,
  companySchema,
  creditBalanceSchema,
  entitlementSchema,
  experience3dSchema,
  listSchema,
  meSchema,
  recommendationSchema,
  imageSearchResponseSchema,
  searchHistoryEntrySchema,
  sessionSchema,
  shareDocumentSchema,
  type CompanyPatch,
  type CropRect,
  type ImageSearchRequest,
  type SearchMode,
} from './types'

/* ==========================================================================
   Endpoint
   --------------------------------------------------------------------------
   Una funzione per operazione, tipizzata in entrata e validata in uscita.
   Nessun componente costruisce un percorso o conosce la forma del payload.
   ========================================================================== */

/**
 * I filtri sui metadati in query string: `f.materiale=marmo`,
 * `f.colori=avorio,beige`, `f.larghezza=100..200`.
 *
 * Il prefisso `f.` non collide con `q`, `type`, `limit` e `cursor`, e tiene la
 * richiesta leggibile a occhio nudo — cosa che conta, visto che esiste un dev
 * server costruito apposta per provare le chiamate a mano.
 */
function metaFilterQuery(meta: CatalogFilters['meta']): Record<string, QueryValue | undefined> {
  if (!meta) return {}
  const query: Record<string, QueryValue | undefined> = {}
  for (const [key, value] of Object.entries(meta)) {
    if (value === undefined || value === '') continue
    if (typeof value === 'string') {
      query[`f.${key}`] = value
    } else if (Array.isArray(value)) {
      if (value.length > 0) query[`f.${key}`] = value
    } else {
      const range = value as { from?: number; to?: number }
      const from = range.from ?? ''
      const to = range.to ?? ''
      if (from !== '' || to !== '') query[`f.${key}`] = `${from}..${to}`
    }
  }
  return query
}

export const catalogApi = {
  /** Il registro dei metadati dell'azienda: quali campi esistono e quali filtrano. */
  schema: () => request(catalogSchemaSchema, { method: 'GET', path: '/catalog/schema' }),

  /**
   * `cursor` è opaco: `undefined` per la prima pagina, altrimenti il
   * `nextCursor` ricevuto dalla pagina precedente. `total` nella risposta è
   * quanti elementi passano il filtro, non quanti ne restituisce la ricerca.
   */
  search: (filters: CatalogFilters, cursor?: string) =>
    request(catalogSearchResponseSchema, {
      method: 'GET',
      path: '/search',
      query: {
        q: filters.q,
        similarTo: filters.similarTo,
        type: filters.type,
        cursor,
        ...metaFilterQuery(filters.meta),
      },
    }),

  /**
   * Stesso percorso della ricerca testuale, e non un endpoint suo: con CLIP
   * testo e immagine cadono nello stesso spazio vettoriale, quindi il server
   * fa una ricerca sola e cambia solo l'encoder che produce il vettore. Qui
   * restano due funzioni perché è diverso il payload, non l'operazione.
   *
   * POST e non GET: l'immagine sta nel corpo, non nella query string, che ha
   * un limite di qualche kilobyte. Il cursore viaggia con lei, così la pagina
   * successiva non deve rispedire nulla di diverso dal resto.
   */
  searchByImage: (payload: ImageSearchRequest) =>
    request(imageSearchResponseSchema, {
      method: 'POST',
      path: '/search',
      body: payload,
    }),
}

export const searchHistoryApi = {
  list: () =>
    request(listSchema(searchHistoryEntrySchema), {
      method: 'GET',
      path: '/search-history',
    }).then((response) => response.items),

  record: (input: {
    mode: SearchMode
    label: string
    /** Presente per le ricerche per immagine: la regione scelta dall'utente. */
    crop?: CropRect
  }) =>
    request(searchHistoryEntrySchema, {
      method: 'POST',
      path: '/search-history',
      body: input,
    }),

  /** Soft delete lato server: la voce sparisce dallo storico, senza tornare indietro dal client. */
  remove: (id: string) =>
    request(listSchema(searchHistoryEntrySchema), {
      method: 'DELETE',
      path: `/search-history/${id}`,
    }).then((response) => response.items),
}

export const collectionsApi = {
  list: () =>
    request(listSchema(collectionSchema), { method: 'GET', path: '/collections' }).then(
      (response) => response.items,
    ),

  create: (input: { name: string; itemIds: string[] }) =>
    request(collectionSchema, { method: 'POST', path: '/collections', body: input }),

  addItems: (input: { collectionId: string; itemIds: string[] }) =>
    request(collectionSchema, {
      method: 'POST',
      path: `/collections/${input.collectionId}/items`,
      body: { itemIds: input.itemIds },
    }),

  removeItem: (input: { collectionId: string; itemId: string }) =>
    request(collectionSchema, {
      method: 'DELETE',
      path: `/collections/${input.collectionId}/items/${input.itemId}`,
    }),

  remove: (collectionId: string) =>
    request(listSchema(collectionSchema), {
      method: 'DELETE',
      path: `/collections/${collectionId}`,
    }).then((response) => response.items),
}

export const presentationsApi = {
  listDocuments: () =>
    request(listSchema(shareDocumentSchema), {
      method: 'GET',
      path: '/presentations/documents',
    }).then((response) => response.items),

  createDocument: (collectionId: string) =>
    request(shareDocumentSchema, {
      method: 'POST',
      path: '/presentations/documents',
      body: { collectionId },
    }),

  removeDocument: (documentId: string) =>
    request(listSchema(shareDocumentSchema), {
      method: 'DELETE',
      path: `/presentations/documents/${documentId}`,
    }).then((response) => response.items),

  listExperiences: () =>
    request(listSchema(experience3dSchema), {
      method: 'GET',
      path: '/presentations/experiences',
    }).then((response) => response.items),

  createExperience: (collectionId: string) =>
    request(experience3dSchema, {
      method: 'POST',
      path: '/presentations/experiences',
      body: { collectionId },
    }),

  removeExperience: (experienceId: string) =>
    request(listSchema(experience3dSchema), {
      method: 'DELETE',
      path: `/presentations/experiences/${experienceId}`,
    }).then((response) => response.items),
}

export const entitlementsApi = {
  list: () =>
    request(listSchema(entitlementSchema), { method: 'GET', path: '/entitlements' }).then(
      (response) => response.items,
    ),

  setActive: (input: { module: AddonModuleKey; active: boolean }) =>
    request(entitlementSchema, {
      method: 'PATCH',
      path: `/entitlements/${input.module}`,
      body: { active: input.active },
    }),
}

export const creditsApi = {
  balance: () => request(creditBalanceSchema, { method: 'GET', path: '/credits' }),
}

export const activityApi = {
  list: () =>
    request(listSchema(activityEntrySchema), { method: 'GET', path: '/activity' }).then(
      (response) => response.items,
    ),
}

export const recommendationsApi = {
  list: () =>
    request(listSchema(recommendationSchema), { method: 'GET', path: '/recommendations' }).then(
      (response) => response.items,
    ),

  refresh: () =>
    request(listSchema(recommendationSchema), {
      method: 'POST',
      path: '/recommendations/refresh',
    }).then((response) => response.items),

  accept: (input: { recommendationId: string; collectionId: string }) =>
    request(collectionSchema, {
      method: 'POST',
      path: `/recommendations/${input.recommendationId}/accept`,
      body: { collectionId: input.collectionId },
    }),
}

export const meApi = {
  get: () => request(meSchema, { method: 'GET', path: '/me' }),
}

export const companiesApi = {
  list: () =>
    request(listSchema(companySchema), { method: 'GET', path: '/admin/companies' }).then(
      (response) => response.items,
    ),

  update: (input: { id: string; patch: CompanyPatch }) =>
    request(companySchema, {
      method: 'PATCH',
      path: `/admin/companies/${input.id}`,
      body: companyPatchSchema.parse(input.patch),
    }),
}

const AUTH_MODE = (import.meta.env.VITE_API_MODE as string | undefined) ?? 'mock'

export const authApi = {
  login: (input: { username: string; password: string }) =>
    AUTH_MODE === 'cognito'
      ? cognitoLogin(input)
      : request(sessionSchema, { method: 'POST', path: '/auth/login', body: input }),

  logout: () => {
    if (AUTH_MODE === 'cognito') {
      cognitoLogout()
      return Promise.resolve(null)
    }
    return request(z.null(), { method: 'POST', path: '/auth/logout' })
  },
}
